import findParentLevel from 'lib/seatmaps/findParentLevel';
import svgClasses from 'lib/common/svgClasses';
import { lookupSeat, findSeatElFromSlug } from 'lib/seats/seatFinders';
import { filter, flow, head, map, reject, sortBy } from 'lodash/fp';

/**
 * Find filename from a url
 * Assumes filename matches the seat selector format.
 * e.g. https://example.com/path/to/file/seat-1-A-1.jpg
 */
export function getFileName(url) {
  let filenameWithExt = url.substring(url.lastIndexOf('/') + 1);
  let parts = filenameWithExt.split('.');
  return head(parts);
}

function viewDetailsExtractor(seatData) {
  return function(item) {
    let slug = getFileName(item);
    let el = findSeatElFromSlug(slug, seatData);
    let data = lookupSeat(slug, seatData);

    if (!el || !data) {
      return undefined;
    }

    let killedSeat = false;

    if (svgClasses.hasClass(el, 'seat--killed')) {
      killedSeat = true;
      svgClasses.removeClass(el, 'seat--killed');
    }

    const info = {
      el: el,
      data: data,
      image: item,
      x: el.getBBox().x,
      y: el.getBBox().y
    };

    if (killedSeat) {
      svgClasses.addClass(el, 'seat--killed');
    }

    return info;
  };
}

export function findNearestCandidate(candidateCoords, selectedCoords) {
  /**
   * Rank coordinates relative to selected coordinates
   * As: (abs(x - i) + abs(y - j))^2
   * @return <List>[<Int>]
   */
  let rankBySelected = function(coords) {
    return Math.pow(
      Math.abs(coords.x - selectedCoords.x) +
        Math.abs(coords.y - selectedCoords.y),
      2
    );
  };

  /**
   * [1] Rank based on of selected coordinates
   * [2] take the first (lowest) item
   */
  return flow(
    sortBy(rankBySelected),
    head
  )(candidateCoords);
}

function getSelectedCoords(el) {
  /**
   * [1] if the selected element is a group (i.e) a custom icon
   *     then we need to extract the transform value
   * [2] otherwise it's a regular seat so use the bounding box
   */
  if (el.tagName === 'g') {
    let xforms = el.transform.baseVal; // SVGTransformList
    let firstXForm = xforms.getItem(0); // SVGTransform
    if (firstXForm.type == SVGTransform.SVG_TRANSFORM_TRANSLATE) {
      let firstX = firstXForm.matrix.e;
      let firstY = firstXForm.matrix.f;
      return { x: firstX, y: firstY };
    }
  } else {
    let bbox = el.getBBox();
    return { x: bbox.x, y: bbox.y };
  }
}

export default function findSeatView(seatData, seatViews, selectedSeat) {
  if (!(seatData && seatViews && selectedSeat)) {
    return;
  }

  /**
   * [1] expand data for each seat view
   * [2] remove undefined entries
   * [3] Find items in the same level as the selected seat
   */
  let parentLevel = findParentLevel(selectedSeat.seatEl).id;
  let mappedViewsForLevel = flow(
    map(viewDetailsExtractor(seatData)),
    reject(x => x === undefined),
    filter(view => findParentLevel(view.el).id === parentLevel)
  )(seatViews);

  let selectedCoords = getSelectedCoords(selectedSeat.seatEl);

  return findNearestCandidate(mappedViewsForLevel, selectedCoords);
}



// WEBPACK FOOTER //
// ./src/javascripts/lib/seats/findSeatView.js